import { TDataset } from '../../../models/aibuilder/aibuilder';
import { AI_BUILDER_PASS_MARK } from '../stations/DeliverStation';

export const MIN_SAMPLES_PER_LABEL_IMAGE = 10;
export const MIN_SAMPLES_PER_LABEL_TEXT = 5;
export const IMBALANCE_RATIO_THRESHOLD = 3;

export function getDatasetDiagnostics(dataset: TDataset, accuracy: number | null): string[] {
  const isText = dataset.data_type === 'text';
  const noun = isText ? 'phrase' : 'image';
  const minPerLabel = isText ? MIN_SAMPLES_PER_LABEL_TEXT : MIN_SAMPLES_PER_LABEL_IMAGE;
  const labels = dataset.labels || [];
  const labelNameById = new Map(labels.map((label) => [label.id, label.name]));
  const validSamples = (dataset.samples || []).filter(
    (sample) =>
      !sample.excluded && sample.label_id != null && (isText ? sample.content_text : sample.content_url),
  );

  const issues: string[] = [];

  const groupsByContent = new Map<string, Set<number>>();
  validSamples.forEach((sample) => {
    const key = isText
      ? (sample.content_text as string).trim().toLowerCase()
      : (sample.content_url as string);
    const labelIds = groupsByContent.get(key) || new Set<number>();
    labelIds.add(sample.label_id as number);
    groupsByContent.set(key, labelIds);
  });
  groupsByContent.forEach((labelIds, content) => {
    if (labelIds.size < 2) return;
    const names = Array.from(labelIds)
      .map((id) => labelNameById.get(id))
      .filter(Boolean);
    if (names.length < 2) return;
    const namesText = names.map((name) => `"${name}"`).join(' et ');
    issues.push(
      isText
        ? `La phrase "${content}" est utilisée pour les labels ${namesText}. Un même exemple ne doit servir qu'à un seul label.`
        : `Une même image est utilisée pour les labels ${namesText}. Une image ne doit servir qu'à un seul label.`,
    );
  });

  const countByLabelId = new Map<number, number>();
  validSamples.forEach((sample) => {
    const labelId = sample.label_id as number;
    countByLabelId.set(labelId, (countByLabelId.get(labelId) || 0) + 1);
  });

  labels.forEach((label) => {
    const count = countByLabelId.get(label.id) || 0;
    if (count > 0 && count < minPerLabel) {
      issues.push(
        `Le label "${label.name}" n'a que ${count} ${noun}${count > 1 ? 's' : ''}. Ajoute-en au moins ${minPerLabel} pour de meilleurs résultats.`,
      );
    }
  });

  const nonEmptyLabels = labels.filter((label) => (countByLabelId.get(label.id) || 0) > 0);
  if (nonEmptyLabels.length >= 2) {
    let maxLabel = nonEmptyLabels[0];
    let minLabel = nonEmptyLabels[0];
    nonEmptyLabels.forEach((label) => {
      const count = countByLabelId.get(label.id) || 0;
      if (count > (countByLabelId.get(maxLabel.id) || 0)) maxLabel = label;
      if (count < (countByLabelId.get(minLabel.id) || 0)) minLabel = label;
    });
    const maxCount = countByLabelId.get(maxLabel.id) || 0;
    const minCount = countByLabelId.get(minLabel.id) || 0;
    if (minCount > 0 && maxCount / minCount >= IMBALANCE_RATIO_THRESHOLD) {
      issues.push(
        `Le label "${maxLabel.name}" a beaucoup plus d'exemples (${maxCount}) que "${minLabel.name}" (${minCount}). Essaie d'avoir un nombre équilibré d'exemples par label.`,
      );
    }
  }

  if (accuracy != null && accuracy < AI_BUILDER_PASS_MARK && issues.length === 0) {
    issues.push(
      "La précision est basse mais le dataset semble correct. Vérifie que les exemples d'un même label se ressemblent bien entre eux, et sont différents de ceux des autres labels.",
    );
  }

  return issues;
}
